import { Box, Button, Icon, List, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import LabeledInput from 'modules/core/components/LabeledInput'
import TextArea from 'modules/core/components/TextArea'
import { useForm } from 'modules/core/hooks/useForm'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Group } from 'modules/expenses/hooks/useGroup'
import WithPaywall from 'modules/finance/components/WithPaywall'

import DropZoneBox from '../../common/DropZone'
import CostCenters from '../../expenses/Detail/_Form/Common/CostCenters'
import { GroupNameField } from '../../expenses/New/GroupModal'
import { EditGroupFormValues, GroupFile } from './types'

interface FormProps {
  group: Group
  form: ReturnType<typeof useForm<EditGroupFormValues>>
  canEdit: boolean
  hasCostCentersFeature: boolean
}

interface FilesListProps {
  files: GroupFile[]
  readonly: boolean
  onDeleteFile: (file: GroupFile) => void
}

interface DownloadFileProps {
  file: GroupFile
}

const DownloadFile = ({ file }: DownloadFileProps) => {
  const url = file instanceof File ? URL.createObjectURL(file) : file.url
  if (!url) return null

  return (
    <Button.Router
      type="secondary"
      to={url}
      blankTarget
      rel="noopener noreferrer"
      download={file.name}
      iconName="download"
    />
  )
}

const FilesList = ({ files, readonly, onDeleteFile }: FilesListProps) => {
  if (files.length === 0) return null

  return (
    <List>
      {files.map((file) => (
        <List.Item key={file.name + file.size}>
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Icon.Medium icon="file-filled" color="grey800" />
            <Box maxWidth="s240">
              <List.Title ellipsis>{file.name}</List.Title>
            </Box>
          </Box>
          <Box gap="s8" flexDirection="row" alignItems="center">
            <DownloadFile file={file} />
            {!readonly && (
              <Button type="secondary" onClick={() => onDeleteFile(file)} iconName="delete" />
            )}
          </Box>
        </List.Item>
      ))}
    </List>
  )
}

export const Form = ({ group, form, canEdit, hasCostCentersFeature }: FormProps) => {
  const employee = group.owner

  const onDeleteFile = (file: GroupFile) => {
    form.setFieldValue(
      'files',
      form.values.files.filter((f) => f !== file)
    )

    if (file.id) {
      form.setFieldValue('filesIdToRemove', [...form.values.filesIdToRemove, file.id])
    }
  }

  return (
    <Box gap="s24">
      <GroupNameField
        value={form.values.name}
        error={form.touched.name ? form.errors.name : undefined}
        readonly={!canEdit}
        onChange={(value) => form.setFieldValue('name', value)}
      />
      {employee && group.reviewRequestAt && !hasCostCentersFeature && (
        <WithPaywall features={[Features.finance_cost_center_management]} fallback={<></>}>
          {() => (
            <CostCenters
              employeeId={employee.id}
              activeOn={stringToDate(group.reviewRequestAt, 'backend_datetime').toDate()}
            />
          )}
        </WithPaywall>
      )}
      <TextArea
        label={i18n.t('expenses_v2.forms.fields.expensable_group.description.label')}
        placeholder={i18n.t('expenses_v2.forms.fields.expensable_group.description.placeholder')}
        value={form.values.description}
        error={form.touched.description ? form.errors.description : undefined}
        readonly={!canEdit}
        onChange={(value) => form.setFieldValue('description', value)}
      />
      <LabeledInput
        isLegend
        label={i18n.t('expenses_v2.forms.fields.expensable_group.files.label')}
        readonly={!canEdit}
      >
        <Box gap="s24">
          <Text.Normal weight="medium" size="200">
            {i18n.t('expenses_v2.forms.fields.expensable_group.files.description')}
          </Text.Normal>
          {canEdit && (
            <Box maxHeight="s240">
              <DropZoneBox
                onDrop={(files: GroupFile[]) =>
                  form.setFieldValue('files', [...form.values.files, ...files])
                }
                title={i18n.t('expenses_v2.forms.fields.dropzone.title')}
                description={i18n.t('expenses_v2.forms.fields.dropzone.description')}
                multiple
              />
            </Box>
          )}
          <FilesList files={form.values.files} readonly={!canEdit} onDeleteFile={onDeleteFile} />
        </Box>
      </LabeledInput>
    </Box>
  )
}
